import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../context/AuthContext";
import { RefreshCw, Trash2, Check, Mail } from "lucide-react";

export default function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setBusy(true); setError(null);
    try {
      const r = await axios.get(`${API}/admin/contact-messages`, { withCredentials: true });
      setMessages(r.data || []);
    } catch (e) {
      setError("Could not load messages.");
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    try { await axios.post(`${API}/admin/contact-messages/${id}/read`, {}, { withCredentials: true }); load(); }
    catch { alert("Action failed."); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try { await axios.delete(`${API}/admin/contact-messages/${id}`, { withCredentials: true }); load(); }
    catch { alert("Action failed."); }
  };

  const unreadCount = messages.filter((m) => !m.read).length;

  return (
    <div data-testid="admin-tab-messages">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h2 className="font-heading font-black text-[#0A0A0A] text-2xl tracking-tight">Contact messages</h2>
          <p className="text-sm text-[#2C2C2C]">{unreadCount} unread · {messages.length} total</p>
        </div>
        <button onClick={load} disabled={busy} data-testid="admin-msgs-refresh" className="bg-transparent border-2 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#FFF8E7] rounded-none px-4 py-2 text-sm font-medium inline-flex items-center gap-2"><RefreshCw className="w-4 h-4" /> Refresh</button>
      </div>

      {error && <div className="bg-[#FF2E63] text-[#FFF8E7] border-2 border-[#0A0A0A] p-3 mb-4" role="alert">{error}</div>}

      <div className="space-y-3" data-testid="admin-messages-list">
        {messages.map((m) => (
          <article key={m.id} className={`border-2 border-[#0A0A0A] p-5 ${m.read ? "bg-white" : "bg-[#FFD93D]"}`} data-testid={`admin-msg-${m.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  {!m.read && <span className="text-xs uppercase tracking-[0.16em] font-bold px-2 py-0.5 bg-[#FF2E63] text-[#FFF8E7] border-2 border-[#0A0A0A]">New</span>}
                  <span className="text-xs text-[#2C2C2C] uppercase tracking-[0.14em]">{new Date(m.created_at).toLocaleString()}</span>
                </div>
                <h3 className="font-heading font-bold text-lg text-[#0A0A0A] leading-tight">{m.subject}</h3>
                <div className="text-sm text-[#2C2C2C]">From {m.name} · <a href={`mailto:${m.email}`} className="underline">{m.email}</a></div>
              </div>
              <div className="flex flex-wrap gap-2">
                <a href={`mailto:${m.email}?subject=Re:%20${encodeURIComponent(m.subject)}`} className="bg-[#1A5DFF] text-[#FFF8E7] hover:bg-[#0E4ACC] border-2 border-[#0A0A0A] rounded-none px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> Reply</a>
                {!m.read && <button onClick={() => markRead(m.id)} data-testid={`admin-msg-read-${m.id}`} className="bg-transparent text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#FFF8E7] border-2 border-[#0A0A0A] rounded-none px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> Mark read</button>}
                <button onClick={() => del(m.id)} data-testid={`admin-msg-delete-${m.id}`} className="bg-[#FF2E63] text-[#FFF8E7] hover:bg-[#E0204F] border-2 border-[#0A0A0A] rounded-none px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
              </div>
            </div>
            <p className="mt-3 text-[#0A0A0A] leading-relaxed whitespace-pre-wrap">{m.message}</p>
          </article>
        ))}
        {!busy && messages.length === 0 && <div className="text-[#2C2C2C]">No messages yet.</div>}
      </div>
    </div>
  );
}
